/**
 * Admin API Hook
 *
 * Provides methods for admin-only API calls with authentication.
 */

import { useState, useCallback } from 'react';
import type { SettingCategory, HeroSlide, AdminUser } from '@petmas/shared';
import { apiClient } from '../api';

export function useAdminApi() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Settings
  const getSettings = useCallback(async (category: SettingCategory) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<Record<string, unknown>>(`/admin/settings/${category}`);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (category: SettingCategory, settings: Record<string, unknown>) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.adminRequest(`/admin/settings/${category}`, {
        method: 'PUT',
        body: JSON.stringify({ settings }),
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Hero Slides
  const getHeroSlides = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<HeroSlide[]>('/admin/hero-slides');
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createHeroSlide = useCallback(async (slide: Partial<HeroSlide>) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<HeroSlide>('/admin/hero-slides', {
        method: 'POST',
        body: JSON.stringify(slide),
      });
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateHeroSlide = useCallback(async (id: string, slide: Partial<HeroSlide>) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<HeroSlide>(`/admin/hero-slides/${id}`, {
        method: 'PUT',
        body: JSON.stringify(slide),
      });
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteHeroSlide = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.adminRequest(`/admin/hero-slides/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reorderHeroSlides = useCallback(async (slideIds: string[]) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.adminRequest('/admin/hero-slides/reorder', {
        method: 'PUT',
        body: JSON.stringify({ slideIds }),
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Admin Users
  const getAdminUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<AdminUser[]>('/admin/users');
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createAdminUser = useCallback(async (user: { email: string; password: string; name: string; role: 'superadmin' | 'admin' }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<AdminUser>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(user),
      });
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateAdminUser = useCallback(async (id: string, user: Partial<AdminUser>) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<AdminUser>(`/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(user),
      });
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteAdminUser = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.adminRequest(`/admin/users/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // AI
  const testAIConnection = useCallback(async (provider: string, model: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminRequest<{ response: string }>('/admin/ai/test', {
        method: 'POST',
        body: JSON.stringify({ provider, model }),
      });
      return { success: true, response: result.response };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
      return { success: false, error: err instanceof Error ? err.message : 'Error de conexion' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    error,
    // Settings
    getSettings,
    updateSettings,
    // Hero Slides
    getHeroSlides,
    createHeroSlide,
    updateHeroSlide,
    deleteHeroSlide,
    reorderHeroSlides,
    // Admin Users
    getAdminUsers,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
    // AI
    testAIConnection,
  };
}
